import { fabric } from "fabric"
import { isSpotColor, spotColorName } from "../spot_colors"
import controls from "./controls"
import env from "@/common/env"
import i18n from "@/common/locales"
import { escapeHtml, isTablet, metaNameSign } from "@/common/util"
import Calc from "@/common/util/calculator"
import ObjectMixin from "@/signomatic/app/objects/object_mixin"
import { type ControlObject, type ExtendedCoordinates } from "@/signomatic/app/objects/sign_image"
import Sign from "@/signomatic/app/sign"
import { type RetroParams } from "@/signomatic/app/sign_drawables"
import { correctFontSize } from "@/signomatic/app/sign_validator"
import { arrayUniq } from "@/signomatic/util"
import Fonts, { type Fontdata } from "@/signomatic/util/fonts"
import { error } from "@/signomatic/util/logger"

interface PathData {
  left: number
  top: number
  lineHeight: number
}

export interface LineMetrics {
  left: number
  top: number
  height: number
  width: number
}

const stateType = {}

interface BrailleCollection {
  [index: string]: Array<number>
}

const brailleCollection: BrailleCollection = {
  a: [1, 0, 0, 0, 0, 0],
  b: [1, 0, 1, 0, 0, 0],
  c: [1, 1, 0, 0, 0, 0],
  d: [1, 1, 0, 1, 0, 0],
  e: [1, 0, 0, 1, 0, 0],
  f: [1, 1, 1, 0, 0, 0],
  g: [1, 1, 1, 1, 0, 0],
  h: [1, 0, 1, 1, 0, 0],
  i: [0, 1, 1, 0, 0, 0],
  j: [0, 1, 1, 1, 0, 0],
  k: [1, 0, 0, 0, 1, 0],
  l: [1, 0, 1, 0, 1, 0],
  m: [1, 1, 0, 0, 1, 0],
  n: [1, 1, 0, 1, 1, 0],
  o: [1, 0, 0, 1, 1, 0],
  p: [1, 1, 1, 0, 1, 0],
  q: [1, 1, 1, 1, 1, 0],
  r: [1, 0, 1, 1, 1, 0],
  s: [0, 1, 1, 0, 1, 0],
  t: [0, 1, 1, 1, 1, 0],
  u: [1, 0, 0, 0, 1, 1],
  v: [1, 0, 1, 0, 1, 1],
  x: [1, 1, 0, 0, 1, 1],
  y: [1, 1, 0, 1, 1, 1],
  z: [1, 0, 0, 1, 1, 1],
  å: [1, 0, 0, 0, 0, 1],
  ö: [0, 1, 1, 0, 0, 1],
  ø: [0, 1, 1, 0, 0, 1],
  w: [0, 1, 1, 1, 0, 1],
  ä: [0, 1, 0, 1, 1, 0],
  æ: [0, 1, 0, 1, 1, 0],
  "&": [1, 1, 1, 0, 1, 1],
  ",": [0, 0, 1, 0, 0, 0],
  ".": [0, 0, 0, 0, 1, 0],
  "'": [0, 0, 0, 1, 0, 0],
  "?": [0, 0, 1, 0, 0, 1],
  "!": [0, 0, 1, 1, 1, 0],
  ":": [0, 0, 1, 1, 0, 0],
  "*": [0, 0, 0, 1, 1, 0],
  " ": [0, 0, 0, 0, 0, 0],
  digit: [0, 1, 0, 1, 1, 1],
  cap: [0, 0, 0, 0, 0, 1],
  capend: [1, 0, 0, 1, 0, 1],
}

const brailleNumbers = ["j", "a", "b", "c", "d", "e", "f", "g", "h", "i"]

const minimaticEllipsePadding = (top: number) => {
  if (top < 10) {
    return 30
  } else if (top < 20) {
    return 43
  } else if (top < 25) {
    return 52
  } else if (top < 30) {
    return 68
  } else if (top < 35) {
    return 78
  } else if (top < 40) {
    return 87
  } else if (top < 45) {
    return 108
  } else if (top < 52) {
    return 162
  } else if (top < 85) {
    return 70
  }

  return 0
}

const minimaticBulgePadding = (top: number) => {
  if (top < 20) {
    return 20
  } else if (top < 30) {
    return 26
  } else if (top < 48) {
    return 46
  } else if (top < 55) {
    return 76
  }

  return 0
}

const minimaticRetroPadding = (textObj: SignText, retroParams: RetroParams, signHeight: number) => {
  const justifyCenter = textObj.state.justify == "center"
  const multiplier = justifyCenter ? 2 : 1
  const extra = multiplier * 3
  const top = textObj.state.top
  const retroCornerMax = retroParams?.distance + retroParams?.strokeWidth + retroParams?.radius

  if (top <= 0) {
    const fromTop = signHeight / 2 + top - (textObj.height || 0) / 2

    if (fromTop < retroCornerMax) {
      return retroCornerMax * multiplier + extra
    }
  }

  if (top > 0) {
    const fromBottom = signHeight / 2 - (top + (textObj.height || 0) / 2)

    if (fromBottom < retroCornerMax) {
      return retroCornerMax * multiplier + extra
    }
  }

  return (retroParams?.distance + retroParams?.strokeWidth) * multiplier + extra
}

const minimaticRoundedPadding = (textObj: SignText, state: SignState) => {
  const justifyCenter = textObj.state.justify == "center"
  const multiplier = justifyCenter ? 2 : 1
  const extra = multiplier * 3

  return Math.max(Math.min(state.widthPx, state.heightPx) * 0.042, Calc.mmToPx(2.5)) * multiplier + extra
}

export const calcMinimaticExtraPadding = (
  state: SignState,
  textObj: SignText,
  signType: string | null,
  borderWidth: number,
  retroParams: RetroParams
) => {
  const shape = state.shape
  const border = state.border

  if (shape === "ellipse") {
    return minimaticEllipsePadding(Math.abs(textObj.state.top) + (textObj.height || 0) / 2)
  } else if (shape === "bulge") {
    return minimaticBulgePadding(Math.abs(textObj.state.top) + (textObj.height || 0) / 2)
  } else if (
    ["rect", "round"].includes(shape) &&
    !metaNameSign(signType || "") &&
    borderWidth !== 0 &&
    !["retro", "rounded"].includes(border)
  ) {
    return Calc.mmToPx(4) + borderWidth * 2
  } else if (border === "retro") {
    return minimaticRetroPadding(textObj, retroParams, state.heightPx)
  } else if (border === "rounded") {
    return minimaticRoundedPadding(textObj, state)
  }

  return Calc.mmToPx(4)
}

export const getCableTagSize = (allTexts: SignText[], width: number): number => {
  const canvasWidthPx = Calc.mmToPx(width)
  const config = cableTagTextConfig(width)
  const marginPx = Calc.mmToPx(config.nonPrintableMargin)
  const totalAllowedWidth = canvasWidthPx - marginPx * 2
  let size = config.maxSize[allTexts.length]

  allTexts.forEach(obj => {
    const currentWidth = obj.getScaledWidth()
    const currentSize = obj.state.size || size

    const scale = totalAllowedWidth / currentWidth
    const scaledSize = currentSize * scale

    if (scaledSize < size) {
      size = scaledSize
    }
  })

  return size
}

export const isCableTagTextOverflowing = (target: SignText, width: number) => {
  const canvasWidthPx = Calc.mmToPx(width)

  const config = cableTagTextConfig(width)
  const marginPx = Calc.mmToPx(config.nonPrintableMargin)
  const totalAllowedWidth = canvasWidthPx - marginPx * 2

  const currentWidth = target.getScaledWidth()

  return totalAllowedWidth < currentWidth
}

export const cableTagTextConfig = (width: number) => {
  // Scale factor converts mm to text size to ensure proper textbox height in cable labels.
  const SCALE_FACTOR = 0.64

  const baseData: {
    [key: number]: {
      maxTextCount: number
      nonPrintableMargin: number
      maxTextHeight: { [line: number]: number }
      minTextHeight: { [line: number]: number }
    }
  } = {
    75: {
      maxTextCount: 3,
      nonPrintableMargin: 10,
      maxTextHeight: {
        1: 10,
        2: 5,
        3: 4,
      },
      minTextHeight: {
        1: 4,
        2: 4,
        3: 3,
      },
    },
    60: {
      maxTextCount: 3,
      nonPrintableMargin: 10,
      maxTextHeight: {
        1: 8,
        2: 4,
        3: 3,
      },
      minTextHeight: {
        1: 3,
        2: 3,
        3: 2.5,
      },
    },
    30: {
      maxTextCount: 2,
      nonPrintableMargin: 5,
      maxTextHeight: {
        1: 6,
        2: 3,
      },
      minTextHeight: {
        1: 3,
        2: 2.5,
      },
    },
  }

  const entry = baseData[width]

  const scaleHeights = (heights: { [key: number]: number }) =>
    Object.fromEntries(
      Object.entries(heights).map(([line, value]) => [Number(line), +(value * SCALE_FACTOR).toFixed(2)])
    )

  return {
    maxTextCount: entry.maxTextCount,
    nonPrintableMargin: entry.nonPrintableMargin,
    maxSize: scaleHeights(entry.maxTextHeight),
    minSize: scaleHeights(entry.minTextHeight),
  }
}

class SignText extends ObjectMixin(fabric.IText, stateType as SignTextState) {
  oCoords: ExtendedCoordinates

  static async fromState(
    sign: Sign,
    state: SignTextState,
    centerX: number,
    centerY: number,
    fontdata: Fontdata,
    isContour?: boolean
  ): Promise<SignText> {
    const fontFace = state.font.toLowerCase()

    if (fontFace != "braille (se 6p)") {
      try {
        await Fonts.loadFont(fontFace, fontdata)
      } catch (e) {
        error(e)
      }
    }

    if (sign.minimatic) {
      if (state.justify === "right") {
        state.left = state.left + state.width / 2
      } else if (state.justify === "left") {
        state.left = state.left - state.width / 2
      }
    }

    if (sign.minimatic && isContour) {
      state.justify = "center"
    }

    return this.create(state, centerX, centerY, sign.minimatic).attachSign(sign).attachState(state).addPlaceholder()
  }

  static create(
    state: SignTextState,
    centerX: number,
    centerY: number,
    minimatic?: boolean,
    isContour?: boolean
  ): SignText {
    const placeholder = i18n.t("signomatic.your_text_here")

    const obj = new SignText(state.text, {
      left: centerX + state.left,
      top: centerY + state.top,
      fontSize: Calc.fontsizeToPx(state.size),
      fontFamily: state.font,
      fontStyle: state.italic ? "italic" : "normal",
      fontWeight: state.bold ? "bold" : "normal",
      underline: state.underlined,
      flipX: state.mirrored ? true : false,
      textAlign: state.justify,
      fill: state.color,
      angle: state.angle,
      originX: minimatic && !isContour ? state.justify : "center",
      originY: "center",
      padding: 0,
      lineHeight: 1,
      cornerSize: 14,
      touchCornerSize: 30,
      transparentCorners: false,
      hoverCursor: minimatic ? "cursor" : "pointer",
      lockScalingFlip: true,
      editable: !isTablet() && state.font !== "Braille (SE 6p)",
      lockSkewingX: true,
      lockSkewingY: true,
      lockUniScaling: true,
      borderColor: "#0374e3",
      borderScaleFactor: 2,
      cornerColor: "#ffffff",
      cornerStrokeColor: "#0374e3",
      cornerStyle: "circle",
      rotatingPointOffset: 20,
      hasControls: !minimatic,
      lockMovementX: minimatic,
      lockMovementY: minimatic,
      selectable: !minimatic,
    })
      .addClass("object", "text")
      .setControlsVisibility({ mt: false, mb: false, ml: false, mr: false })
      .addPlaceholder(placeholder, true)

    obj.controls = controls

    return obj
  }

  static stateToObject(state: SignTextState, obj: SignText, centerX: number, centerY: number): Partial<SignText> {
    const newState: Partial<SignText> = {}

    if (Object.prototype.hasOwnProperty.call(state, "text")) {
      newState.text = state.text
    }

    if (Object.prototype.hasOwnProperty.call(state, "bold")) {
      newState.fontWeight = state.bold ? "bold" : "normal"
    }

    if (Object.prototype.hasOwnProperty.call(state, "italic")) {
      newState.fontStyle = state.italic ? "italic" : "normal"
    }

    if (state.font) {
      newState.fontFamily = state.font
    }

    if (Object.prototype.hasOwnProperty.call(state, "underlined")) {
      newState.underline = state.underlined
    }

    if (state.justify) {
      newState.textAlign = state.justify
    }

    if (Object.prototype.hasOwnProperty.call(state, "mirrored")) {
      newState.flipX = state.mirrored
    }

    if (state.color) {
      newState.fill = state.color
    }

    if (Object.prototype.hasOwnProperty.call(state, "size")) {
      newState.fontSize = Calc.fontsizeToPx(state.size / (obj.scaleX || 1))
    }

    if (Object.prototype.hasOwnProperty.call(state, "top")) {
      newState.top = centerY + state.top
    }

    if (Object.prototype.hasOwnProperty.call(state, "left")) {
      newState.left = centerX + state.left
    }

    if (Object.prototype.hasOwnProperty.call(state, "angle")) {
      newState.angle = state.angle
    }

    return newState
  }

  private pathData: PathData[] = []

  scaleStylesFontSizes(data: SignData) {
    const styleRows = Object.keys(this.styles)

    if (styleRows.length > 0) {
      // Iterate lines
      for (const styleRow of styleRows) {
        const row = this.styles[styleRow]
        const styleChars = Object.keys(row)

        // Iterate characters
        for (const styleChar of styleChars) {
          const charStyle = row[styleChar]

          if (charStyle.fontSize) {
            // Scale fontSize
            const size = correctFontSize(
              (this.scaleX || 1) * charStyle.fontSize,
              data,
              this.attachedSign.state,
              this.state.font,
              this.state.text
            )
            charStyle.fontSize = size
          }
        }
      }
    }

    this.clearMetricsCache()
  }

  toObject(): SignObjectV2 {
    let border = {}

    if (this.state.border && this.state.border.shape) {
      border = {
        objectBorderType: this.state.border.shape,
        objectBorderFill: this.state.border.color,
        objectBorderColor: this.state.border.borderColor,
        objectBorderSize: this.state.border.size,
        objectBorderThickness: this.state.border.thickness,
      }
    }

    return {
      ...super.toObject(),
      ...border,
      zIndex: this.state.zindex,
      oIndex: this.state.oindex,
      fill: this.state.color,
      flipX: this.state.mirrored,
      fontFamily: this.state.font,
      fontWeight: this.state.bold ? "bold" : "normal",
      fontStyle: this.state.italic ? "italic" : "normal",
      textDecoration: this.state.underlined ? "underline" : "none",
      textAlign: this.state.justify,
      side: this.state.side,
    }
  }

  lineMetrics(): LineMetrics[] {
    const lines = this.text?.split(/\r?\n/) || []
    let lineHeights = 0

    return lines.map((line, index) => {
      const bounds = this.getBoundingRect(true)
      const data = this.pathData[index]
      const left = data ? bounds.left + (this.width || 0) / 2 + data.left : 0
      const top = data ? bounds.top + lineHeights : 0
      const height = data ? data.lineHeight : 0
      const measure = this.measureLine(index)

      lineHeights += data ? data.lineHeight : 0

      return {
        left,
        top,
        height,
        width: measure.width,
      }
    })
  }

  attachSign(sign: Sign) {
    sign.onOpentypeToggle.subscribe(() => this.clearMetricsCache())
    return super.attachSign(sign)
  }

  hasGlyphs(text: string) {
    if (!Sign.opentypeText) {
      return true
    }

    text = text.replace(/\r?\n|\r/g, "")
    const font = this.getFont()

    if (font) {
      return !font.stringToGlyphs(text, { ...font.defaultRenderOptions, kerning: false }).some(g => !g.unicode)
    }

    return true
  }

  getFont() {
    let variant = "normal"

    if (this.fontFamily === "Braille (SE 6p)") {
      return null
    }

    if (this.fontStyle === "italic" && this.fontWeight === "bold") {
      variant = "bold_italic"
    } else if (this.fontWeight === "bold") {
      variant = "bold"
    } else if (this.fontStyle === "italic") {
      variant = "italic"
    }

    if (!this.fontFamily) {
      return null
    }

    return Fonts.getOpentypeFont(this.fontFamily, variant)
  }

  textToBraille(line: string) {
    const text = line as unknown
    const textArray = typeof line === "string" ? line.split("") : (text as Array<string>)

    const brailleText = textArray.flatMap(c => {
      // Char is number
      if (!isNaN(parseInt(c))) {
        return ["digit", brailleNumbers[parseInt(c)]]
      }

      return c
    })

    return brailleText
  }

  private get brailleParams() {
    return {
      radius: Calc.mmToPx(0.75),
      dotDistance: Calc.mmToPx(2.5),
      glyphDistanceX: Calc.mmToPx(3.5) + Calc.mmToPx(2.5),
    }
  }

  brailleFont(line: string, lineIndex: number, ctx: CanvasRenderingContext2D, left: number, top: number) {
    const drawBraille = (brailleMap: Array<number>, left: number) => {
      const topDistance = top - 20

      brailleMap.forEach((value, i) => {
        if (value == 0) {
          return
        }

        ctx.beginPath()

        if (i == 0) {
          ctx.arc(left, topDistance, this.brailleParams.radius, 0, 2 * Math.PI, false)
        } else if (i == 1) {
          ctx.arc(left + this.brailleParams.dotDistance, topDistance, this.brailleParams.radius, 0, 2 * Math.PI, false)
        } else if (i == 2) {
          ctx.arc(left, topDistance + this.brailleParams.dotDistance, this.brailleParams.radius, 0, 2 * Math.PI, false)
        } else if (i == 3) {
          ctx.arc(
            left + this.brailleParams.dotDistance,
            topDistance + this.brailleParams.dotDistance,
            this.brailleParams.radius,
            0,
            2 * Math.PI,
            false
          )
        } else if (i == 4) {
          ctx.arc(
            left,
            topDistance + this.brailleParams.dotDistance * 2,
            this.brailleParams.radius,
            0,
            2 * Math.PI,
            false
          )
        } else {
          ctx.arc(
            left + this.brailleParams.dotDistance,
            topDistance + this.brailleParams.dotDistance * 2,
            this.brailleParams.radius,
            0,
            2 * Math.PI,
            false
          )
        }

        ctx.fillStyle = this.state.color

        ctx.fill()
      })

      ctx.save
    }

    this.textToBraille(line).forEach((value: string, i) => {
      if (brailleCollection[value.toLowerCase()] === undefined) {
        return
      }

      const distanceX = left + 7 + this.brailleParams.glyphDistanceX * i

      drawBraille(brailleCollection[value.toLowerCase()], distanceX)
    })
  }

  brailleSvgPath(text: string[], lineIndex: number, left: number, top: number) {
    const svgPath = [] as any

    const brailleToSvg = (brailleMap: Array<number>, x: number, y: number) => {
      const r = this.brailleParams.radius

      brailleMap.forEach((value, i) => {
        if (value == 0) {
          return
        }

        if (i == 0) {
          svgPath.push(["M", x, y, "m", -r, "0a", r, r, 0, 1, 0, r * 2, "0a", r, r, 0, 1, 0, -r * 2, 0])
        } else if (i == 1) {
          svgPath.push([
            "M",
            x + this.brailleParams.dotDistance,
            y,
            "m",
            -r,
            "0a",
            r,
            r,
            0,
            1,
            0,
            r * 2,
            "0a",
            r,
            r,
            0,
            1,
            0,
            -r * 2,
            0,
          ])
        } else if (i == 2) {
          svgPath.push([
            "M",
            x,
            y + this.brailleParams.dotDistance,
            "m",
            -r,
            "0a",
            r,
            r,
            0,
            1,
            0,
            r * 2,
            "0a",
            r,
            r,
            0,
            1,
            0,
            -r * 2,
            0,
          ])
        } else if (i == 3) {
          svgPath.push([
            "M",
            x + this.brailleParams.dotDistance,
            y + this.brailleParams.dotDistance,
            "m",
            -r,
            "0a",
            r,
            r,
            0,
            1,
            0,
            r * 2,
            "0a",
            r,
            r,
            0,
            1,
            0,
            -r * 2,
            0,
          ])
        } else if (i == 4) {
          svgPath.push([
            "M",
            x,
            y + this.brailleParams.dotDistance * 2,
            "m",
            -r,
            "0a",
            r,
            r,
            0,
            1,
            0,
            r * 2,
            "0a",
            r,
            r,
            0,
            1,
            0,
            -r * 2,
            0,
          ])
        } else {
          svgPath.push([
            "M",
            x + this.brailleParams.dotDistance,
            y + this.brailleParams.dotDistance * 2,
            "m",
            -r,
            "0a",
            r,
            r,
            0,
            1,
            0,
            r * 2,
            "0a",
            r,
            r,
            0,
            1,
            0,
            -r * 2,
            0,
          ])
        }
      })
    }

    text.forEach((value: string, i) => {
      if (brailleCollection[value.toLowerCase()] === undefined) {
        return
      }

      const distanceX = left + 7 + this.brailleParams.glyphDistanceX * i
      const topDistance = top - 20

      brailleToSvg(brailleCollection[value.toLowerCase()], distanceX, topDistance)
    })

    return svgPath.flat().join(" ")
  }

  /**
   * Opentype overrides
   * Research:
   * * https://github.com/shyiko/canvas-text-opentypejs-shim
   * * https://github.com/Q42/fabricjs-opentypejs-demo
   * * https://github.com/Tod314/fabric.CurvesText
   */

  // https://github.com/fabricjs/fabric.js/blob/v460/src/shapes/text.class.js#L684
  getFontCache(decl: fabric.TextStyle) {
    const fontFamily = decl.fontFamily?.toLowerCase() || ""

    if (!fabric.charWidthsCache[fontFamily]) {
      fabric.charWidthsCache[fontFamily] = {}
    }

    const cache = fabric.charWidthsCache[fontFamily] || {}

    const cacheProp = decl.fontStyle?.toLowerCase() + "_" + (decl.fontWeight + "").toLowerCase() + "_" + this.fontSize

    return cache[cacheProp] || {}
  }

  // https://github.com/fabricjs/fabric.js/blob/v460/src/shapes/text.class.js#L1008
  // lacking support for text on paths and RTL writing from fabric 4
  _renderChars(
    method: string,
    ctx: CanvasRenderingContext2D,
    line: string,
    left: number,
    top: number,
    lineIndex: number
  ) {
    if (!Sign.opentypeText) {
      return super._renderChars(method, ctx, line, left, top, lineIndex)
    }

    const lineHeight = this.getHeightOfLine(lineIndex)
    const font = this.getFont()

    if (font) {
      top -= (lineHeight * this._fontSizeFraction) / (this.lineHeight || 0)
      const path = font.getPath(line, left, top, this.fontSize || 0)
      path.fill = this.fill as string

      path.draw(ctx)
      ctx.save()
    }

    if (this.fontFamily === "Braille (SE 6p)" && line.length > 0) {
      top -= (lineHeight * this._fontSizeFraction) / (this.lineHeight || 0)
      this.brailleFont(line, lineIndex, ctx, left, top)
    }

    this.pathData[lineIndex] = {
      left,
      top,
      lineHeight,
    }
  }

  // https://github.com/fabricjs/fabric.js/blob/v451/src/shapes/text.class.js#L840
  getHeightOfLine(lineIndex: number) {
    if (this.fontFamily !== "Braille (SE 6p)") {
      const h = super.getHeightOfLine(lineIndex)

      if (this.attachedSign?.signType == "contour_cut") {
        return h * 1.13
      }

      return h
    }

    return 19 + Calc.mmToPx(5)
  }

  // https://github.com/fabricjs/fabric.js/blob/v451/src/shapes/text.class.js#L670
  _measureChar(char: string, charStyle: fabric.TextStyle, previousChar: string, prevCharStyle: fabric.TextStyle) {
    if (!Sign.opentypeText) {
      return super._measureChar(char, charStyle, previousChar, prevCharStyle)
    }

    const fontCache = this.getFontCache(charStyle)
    const fontDeclaration = this._getFontDeclaration(charStyle)
    const previousFontDeclaration = this._getFontDeclaration(prevCharStyle)
    const couple = previousChar + char
    const stylesAreEqual = fontDeclaration === previousFontDeclaration
    let width
    let coupleWidth
    let previousWidth
    let kernedWidth

    if (previousChar && fontCache[previousChar] !== undefined) {
      previousWidth = fontCache[previousChar]
    }
    if (fontCache[char] !== undefined) {
      kernedWidth = width = fontCache[char]
    }
    if (stylesAreEqual && fontCache[couple] !== undefined) {
      coupleWidth = fontCache[couple] || 0
      kernedWidth = coupleWidth - (previousWidth || 0)
    }

    const font = this.getFont()

    if (font) {
      if (width === undefined) {
        kernedWidth = width = font.getAdvanceWidth(char + "", charStyle.fontSize)
        fontCache[char] = width
      }

      if (previousWidth === undefined && stylesAreEqual && previousChar) {
        previousWidth = font.getAdvanceWidth(previousChar + "", prevCharStyle.fontSize)
        fontCache[previousChar] = previousWidth
      }
      if (stylesAreEqual && coupleWidth === undefined) {
        coupleWidth = font.getAdvanceWidth(couple + "", prevCharStyle.fontSize)
        fontCache[couple] = coupleWidth
        kernedWidth = coupleWidth - (previousWidth || 0)
      }
    }

    return { width: width || 0, kernedWidth: kernedWidth || 0 }
  }

  // https://github.com/fabricjs/fabric.js/blob/v451/src/shapes/text.class.js#L724
  measureLine(lineIndex: number) {
    if (this.fontFamily !== "Braille (SE 6p)") {
      return super.measureLine(lineIndex)
    }

    const lineInfo = this._measureLine(lineIndex)
    let lineLength = 0

    for (let i = 0; i < this._textLines.length; i++) {
      const line = this.textToBraille((this._textLines[i] || []).join(""))

      if (line.length > lineLength) {
        lineLength = line.length
      }
    }

    if (this.charSpacing !== 0) {
      lineInfo.width -= this._getWidthOfCharSpacing()
    }
    if (lineInfo.width < 0) {
      lineInfo.width = 0
    }

    if (this.fontFamily === "Braille (SE 6p)") {
      lineInfo.width = 22.8 * lineLength
    }

    return lineInfo
  }

  // https://github.com/fabricjs/fabric.js/blob/v451/src/mixins/itext.svg_export.js#L45
  _wrapSVGTextAndBg(textAndBg?: any) {
    if (!Sign.opentypeText) {
      return super._wrapSVGTextAndBg(textAndBg)
    }

    const offsets = this._getSVGLeftTopOffsets()
    let result = ""
    let decoration = ""

    const font = this.getFont()

    for (let i = 0; i < this._textLines.length; i++) {
      const pos = {
        left: offsets.textLeft + (this.width || 0) / 2 + this.pathData[i].left,
        top: offsets.textTop + (this.height || 0) / 2 + this.pathData[i].top,
      }

      const line = (this._textLines[i] || []).join("")
      let svg = ""

      if (this.fontFamily === "Braille (SE 6p)") {
        svg = `<path d='${this.brailleSvgPath(this.textToBraille(line), i, pos.left, pos.top)}' fill='${
          this.state.color
        }' />`
      } else if (font) {
        const path = font.getPath(line, pos.left, pos.top, this.fontSize || 0)
        path.fill = this.fill as string

        if (this.stroke) {
          path.stroke = this.stroke
        }

        if (this.strokeWidth) {
          path.strokeWidth = this.strokeWidth
        }
        // add metadata about the text object to the path element
        svg = path.toSVG(2)
      }

      const close = svg.lastIndexOf("/>")

      const attrs: string[] = []
      attrs.push(`class="${arrayUniq(this.classes).join(" ")}"`)

      if (typeof this.fill === "string") {
        if (this.fontFamily === "Braille (SE 6p)") {
          attrs.push("spotfill='hole_braille'")
        } else if (isSpotColor(this.fill)) {
          attrs.push(`spotfill="${spotColorName(this.fill)}"`)
        }
      }

      if (env.TEST) {
        attrs.push(`text="${escapeHtml(line)}"`)
        attrs.push(`size="${+Calc.pxToFontsize(this.fontSize || 0).toFixed(1)}"`)
        attrs.push(`fontweight="${this.fontWeight}"`)
        attrs.push(`fontstyle="${this.fontStyle}"`)
      }

      result += [svg.slice(0, close), ` ${attrs.join(" ")} `, svg.slice(close)].join("")
    }

    if (this.underline) {
      decoration = this._svgTextDecoration("underline")
    }

    return [result, decoration]
  }

  _setCornerCoords(): void {
    const coords = this.oCoords
    const controlKeys = ["tl", "mt", "tr", "ml", "mr", "bl", "mb", "br", "mtr", "move"]

    for (const controlKey of controlKeys) {
      const controlObject = this.controls[controlKey] as ControlObject

      if (!controlObject || !controlObject.calcCornerCoords || !coords) {
        continue
      }

      const coord = coords[controlKey] as any

      if (!coord) {
        continue
      }

      coord.corner = controlObject.calcCornerCoords(
        this.angle || 0,
        this.cornerSize || 0,
        coord.x,
        coord.y,
        false,
        this
      )
      coord.touchCorner = controlObject.calcCornerCoords(
        this.angle || 0,
        this.touchCornerSize || 0,
        coord.x,
        coord.y,
        true,
        this
      )
    }
  }

  // Based on https://github.com/fabricjs/fabric.js/blob/v451/src/shapes/text.class.js#L1306
  private _svgTextDecoration(type: string): string {
    const rects: string[] = []
    let heightOfLine: number
    let size: number
    let charSize: number
    let lineLeftOffset: number
    let dy: number
    let charDY: number
    let line: string[]
    let lastDecoration: string | null
    const leftOffset = this._getLeftOffset()
    let topOffset = this._getTopOffset()
    let top: number
    let boxStart: number
    let boxWidth: number
    let charBox:
      | {
          width: number
          left: number
          height?: number | undefined
          kernedWidth?: number | undefined
          deltaY?: number | undefined
        }
      | undefined
    let currentDecoration: string | null = null
    let maxHeight: number
    let currentStroke: string | null = null
    let currentstrokeWidth: number | null = null
    let currentFill: string | null = null
    let lastFill: string | null = null
    const charSpacing = this._getWidthOfCharSpacing()
    const fraction = fabric.Object.NUM_FRACTION_DIGITS || 3

    for (let i = 0, len = this._textLines.length; i < len; i++) {
      heightOfLine = this.getHeightOfLine(i)
      line = this._textLines[i]
      maxHeight = heightOfLine / (this.lineHeight || 0)
      lineLeftOffset = this._getLineLeftOffset(i)
      boxStart = 0
      boxWidth = 0
      lastDecoration = this.getValueOfPropertyAt(i, 0, type)
      lastFill = this.getValueOfPropertyAt(i, 0, "fill")
      top = topOffset + maxHeight * (1 - this._fontSizeFraction)
      size = this.getHeightOfChar(i, 0)
      dy = this.getValueOfPropertyAt(i, 0, "deltaY")

      for (let j = 0, jlen = line.length; j < jlen; j++) {
        charBox = this.__charBounds && this.__charBounds[i][j]
        currentDecoration = this.getValueOfPropertyAt(i, j, type)
        currentStroke = this.getValueOfPropertyAt(i, j, "stroke")
        currentstrokeWidth = this.getValueOfPropertyAt(i, j, "strokeWidth")
        currentFill = this.getValueOfPropertyAt(i, j, "fill")
        charSize = this.getHeightOfChar(i, j)
        charDY = this.getValueOfPropertyAt(i, j, "deltaY")

        if (charBox) {
          if (
            (currentDecoration !== lastDecoration || currentFill !== lastFill || charSize !== size || charDY !== dy) &&
            boxWidth > 0
          ) {
            if (lastDecoration && lastFill) {
              const height = (this.fontSize || 0) / 15
              const style = `stroke: ${lastFill}; stroke-width: ${Calc.toFixed(height, fraction)}px; fill-opacity: 0;`
              const x = leftOffset + lineLeftOffset + boxStart
              const y = top + this.offsets[type] * size + dy + height / 2
              const path = `M ${Calc.toFixed(x, fraction)} ${Calc.toFixed(y, fraction)} L ${Calc.toFixed(
                x + boxWidth,
                fraction
              )} ${Calc.toFixed(y, fraction)}`
              rects.push(`<path ${this.underlineAttrs()} d="${path}" style="${style}" />`)
            }

            boxStart = charBox.left
            boxWidth = charBox.width
            lastDecoration = currentDecoration
            lastFill = currentFill
            size = charSize
            dy = charDY
          } else {
            boxWidth += charBox.kernedWidth || 0
          }
        }
      }

      if (currentDecoration && currentFill) {
        const height = (this.fontSize || 0) / 15
        const x = leftOffset + lineLeftOffset + boxStart
        const y = top + this.offsets[type] * size + dy + height / 2
        const width = boxWidth - charSpacing
        let style = `stroke: ${lastFill}; stroke-width: ${Calc.toFixed(height, fraction)}px; fill-opacity: 0;`
        let path = `M ${Calc.toFixed(x, fraction)} ${Calc.toFixed(y, fraction)} L ${Calc.toFixed(
          x + width,
          fraction
        )} ${Calc.toFixed(y, fraction)}`

        if (lastFill === "transparent") {
          style = `stroke: ${currentStroke}; stroke-width: ${currentstrokeWidth}px; fill-opacity: 0;`
          path = `M ${Calc.toFixed(x, fraction)} ${Calc.toFixed(y - height / 2, fraction)} L ${Calc.toFixed(
            x + width,
            fraction
          )} ${Calc.toFixed(y - height / 2, fraction)} L ${Calc.toFixed(x + width, fraction)} ${Calc.toFixed(
            y + height / 2,
            fraction
          )} L ${Calc.toFixed(x, fraction)} ${Calc.toFixed(y + height / 2, fraction)} L ${Calc.toFixed(
            x,
            fraction
          )} ${Calc.toFixed(y - height / 2, fraction)}`
        }

        rects.push(`<path ${this.underlineAttrs()} d="${path}" style="${style}" />`)
      }

      topOffset += heightOfLine
    }

    return rects.join("")
  }

  private underlineAttrs() {
    const attrs = [`class="object textdecoration"`]

    if (typeof this.fill === "string" && isSpotColor(this.fill)) {
      attrs.push(`spotstroke="${spotColorName(this.fill)}"`)
    }

    return attrs.join(" ")
  }

  private clearMetricsCache() {
    this._clearCache()
    fabric.charWidthsCache = {}
    this._forceClearCache = true
  }
}

export default SignText
